import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { TransitionConfig, TransitionPainter, TransitionPhase } from './types';
import { createPainter } from './painters';
import { clamp01 } from './easing';

interface Props {
  phase: TransitionPhase;
  config: TransitionConfig;
}

/**
 * A fixed-position three.js overlay that plays a painter animation
 * while `phase` is 'exit' or 'enter'. Unmounts the renderer on dispose.
 */
export function TransitionCanvas({ phase, config }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const stateRef = useRef<{
    renderer: THREE.WebGLRenderer;
    scene: THREE.Scene;
    camera: THREE.OrthographicCamera;
    painter: TransitionPainter;
    painterType: string;
    animId: number;
    cleanupResize: () => void;
  } | null>(null);

  // Initialize renderer + scene once per component mount.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    // Skip GL init in non-browser / test environments.
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    const testCanvas = document.createElement('canvas');
    if (!testCanvas.getContext('webgl') && !testCanvas.getContext('webgl2')) return;

    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    } catch {
      return;
    }
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setClearColor(0x000000, 0);
    el.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 10);
    camera.position.z = 1;

    const resize = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      renderer.setSize(w, h);
      const aspect = w / h;
      camera.left = -aspect;
      camera.right = aspect;
      camera.top = 1;
      camera.bottom = -1;
      camera.updateProjectionMatrix();
    };
    resize();
    window.addEventListener('resize', resize);

    stateRef.current = {
      renderer,
      scene,
      camera,
      painter: createPainter('none'),
      painterType: '',
      animId: 0,
      cleanupResize: () => window.removeEventListener('resize', resize),
    };

    return () => {
      const state = stateRef.current;
      if (!state) return;
      cancelAnimationFrame(state.animId);
      state.cleanupResize();
      state.painter.dispose();
      state.renderer.dispose();
      state.renderer.domElement.remove();
      stateRef.current = null;
    };
  }, []);

  // Swap painter when config.type changes.
  useEffect(() => {
    const state = stateRef.current;
    if (!state) return;
    if (state.painterType === config.type) return;
    state.painter.dispose();
    const painter = createPainter(config.type);
    const aspect = (state.camera.right - state.camera.left) / 2;
    painter.setup({
      scene: state.scene,
      camera: state.camera,
      aspect,
      color: new THREE.Color(config.color ?? '#000000'),
    });
    painter.hide();
    state.renderer.render(state.scene, state.camera);
    state.painter = painter;
    state.painterType = config.type;
  }, [config.type, config.color]);

  // Drive the animation from phase changes.
  useEffect(() => {
    const state = stateRef.current;
    if (!state) return;

    if (phase === 'idle') {
      state.painter.hide();
      state.renderer.render(state.scene, state.camera);
      cancelAnimationFrame(state.animId);
      return;
    }

    const halfDuration = (config.durationMs ?? 600) / 2 / 1000;
    const start = performance.now();
    const animate = () => {
      const elapsed = (performance.now() - start) / 1000;
      const t = clamp01(elapsed / halfDuration);
      const aspect = (state.camera.right - state.camera.left) / 2;
      state.painter.update(phase, t, {
        scene: state.scene,
        camera: state.camera,
        aspect,
        color: new THREE.Color(config.color ?? '#000000'),
      });
      state.renderer.render(state.scene, state.camera);
      state.animId = requestAnimationFrame(animate);
    };
    state.animId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(state.animId);
  }, [phase, config.durationMs, config.color]);

  return (
    <div
      ref={containerRef}
      className="scn-transition-overlay"
      data-phase={phase}
      data-testid="scn-transition-overlay"
      aria-hidden="true"
    />
  );
}
